import {
  ComplianceMonitoringProgramme,
  ComplianceMonitoringPlanItem,
  ComplianceReview,
  ComplianceReviewRequirement,
  ComplianceReviewAssessment,
  ComplianceReviewStatusHistory,
  ComplianceFinding,
  ComplianceAction,
  ComplianceReport,
  MonitoringDashboardKPIs
} from './types';

const API_BASE = '/api/compliance/monitoring';

export const monitoringService = {
  // KPIs
  async getDashboardKPIs(orgId: string = 'ORG-XYZ'): Promise<MonitoringDashboardKPIs> {
    const res = await fetch(`${API_BASE}/analytics/dashboard?organization_id=${orgId}`);
    if (!res.ok) throw new Error('KPI verileri alinamadi');
    return res.json();
  },

  // Alias for tests/compatibility
  async getMetrics(orgId: string = 'ORG-XYZ'): Promise<MonitoringDashboardKPIs> {
    return this.getDashboardKPIs(orgId);
  },

  // Programmes
  async getProgrammes(orgId: string = 'ORG-XYZ'): Promise<ComplianceMonitoringProgramme[]> {
    const res = await fetch(`${API_BASE}/programmes?organization_id=${orgId}`);
    if (!res.ok) throw new Error('Programlar alinamadi');
    return res.json();
  },

  async getProgrammeById(id: string, orgId: string = 'ORG-XYZ'): Promise<ComplianceMonitoringProgramme> {
    const res = await fetch(`${API_BASE}/programmes/${id}?organization_id=${orgId}`);
    if (!res.ok) throw new Error('Program detayi alinamadi');
    return res.json();
  },

  async createProgramme(data: Partial<ComplianceMonitoringProgramme>, orgId: string = 'ORG-XYZ'): Promise<ComplianceMonitoringProgramme> {
    const res = await fetch(`${API_BASE}/programmes`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'x-organization-id': orgId },
      body: JSON.stringify(data)
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || 'Program olusturulamadi');
    }
    return res.json();
  },

  // Plan Items
  async getPlanItems(programmeId?: string, orgId: string = 'ORG-XYZ'): Promise<ComplianceMonitoringPlanItem[]> {
    if (programmeId) {
      const res = await fetch(`${API_BASE}/programmes/${programmeId}/items?organization_id=${orgId}`);
      if (!res.ok) throw new Error('Plan maddeleri alinamadi');
      return res.json();
    }
    // If no programmeId, fetch for all programmes in the org
    const programmes = await this.getProgrammes(orgId);
    const itemPromises = programmes.map(p =>
      fetch(`${API_BASE}/programmes/${p.id}/items?organization_id=${orgId}`)
        .then(r => (r.ok ? r.json() : []))
        .catch(() => [])
    );
    const itemsArrays = await Promise.all(itemPromises);
    return itemsArrays.flat();
  },

  async addPlanItem(programmeId: string, item: Partial<ComplianceMonitoringPlanItem>, orgId: string = 'ORG-XYZ'): Promise<ComplianceMonitoringPlanItem> {
    const res = await fetch(`${API_BASE}/programmes/${programmeId}/items`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'x-organization-id': orgId },
      body: JSON.stringify(item)
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || 'Plan maddesi eklenemedi');
    }
    return res.json();
  },

  async createPlanItem(item: Partial<ComplianceMonitoringPlanItem> & { programme_id: string }, orgId: string = 'ORG-XYZ'): Promise<ComplianceMonitoringPlanItem> {
    return this.addPlanItem(item.programme_id, item, orgId);
  },

  // Reviews
  async getReviews(orgId: string = 'ORG-XYZ', filters?: {
    status?: string;
    department?: string;
    search?: string;
  }): Promise<ComplianceReview[]> {
    const params = new URLSearchParams({ organization_id: orgId });
    if (filters?.status) params.append('status', filters.status);
    if (filters?.department) params.append('department', filters.department);
    if (filters?.search) params.append('search', filters.search);

    const res = await fetch(`${API_BASE}/reviews?${params.toString()}`);
    if (!res.ok) throw new Error('Inceleme kayitlari alinamadi');
    return res.json();
  },

  async getReviewById(id: string, orgId: string = 'ORG-XYZ'): Promise<ComplianceReview> {
    const res = await fetch(`${API_BASE}/reviews/${id}?organization_id=${orgId}`);
    if (!res.ok) throw new Error('Inceleme detayi alinamadi');
    return res.json();
  },

  async createReview(data: Partial<ComplianceReview>, orgId: string = 'ORG-XYZ'): Promise<ComplianceReview> {
    const res = await fetch(`${API_BASE}/reviews`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'x-organization-id': orgId },
      body: JSON.stringify(data)
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || 'Inceleme olusturulamadi');
    }
    return res.json();
  },

  async transitionReview(id: string, newStatus: string, reason: string, changedBy: string, orgId: string = 'ORG-XYZ'): Promise<ComplianceReview> {
    const res = await fetch(`${API_BASE}/reviews/${id}/transition`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'x-organization-id': orgId },
      body: JSON.stringify({ new_status: newStatus, reason, changed_by: changedBy })
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || 'Durum gecisi basarisiz');
    }
    return res.json();
  },

  async updateReviewStatus(id: string, newStatus: string, reason: string, changedBy: string, orgId: string = 'ORG-XYZ'): Promise<ComplianceReview> {
    return this.transitionReview(id, newStatus, reason, changedBy, orgId);
  },

  // Requirements Linkage
  async getReviewRequirements(reviewId: string, orgId: string = 'ORG-XYZ'): Promise<ComplianceReviewRequirement[]> {
    const res = await fetch(`${API_BASE}/reviews/${reviewId}/requirements?organization_id=${orgId}`);
    if (!res.ok) throw new Error('Inceleme gereksinimleri alinamadi');
    return res.json();
  },

  async addReviewRequirement(reviewId: string, req: Partial<ComplianceReviewRequirement>, orgId: string = 'ORG-XYZ'): Promise<ComplianceReviewRequirement> {
    const res = await fetch(`${API_BASE}/reviews/${reviewId}/requirements`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'x-organization-id': orgId },
      body: JSON.stringify(req)
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || 'Gereksinim eklenemedi');
    }
    return res.json();
  },

  async linkRequirement(reviewId: string, req: Partial<ComplianceReviewRequirement>, orgId: string = 'ORG-XYZ'): Promise<ComplianceReviewRequirement> {
    return this.addReviewRequirement(reviewId, req, orgId);
  },

  // Assessments
  async recordAssessment(reviewId: string, asmt: Partial<ComplianceReviewAssessment>, orgId: string = 'ORG-XYZ'): Promise<ComplianceReviewAssessment> {
    const res = await fetch(`${API_BASE}/reviews/${reviewId}/assessments`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'x-organization-id': orgId },
      body: JSON.stringify(asmt)
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || 'Degerlendirme kaydedilemedi');
    }
    return res.json();
  },

  async getAssessments(reviewId: string, orgId: string = 'ORG-XYZ'): Promise<ComplianceReviewAssessment[]> {
    // Requirements for a review also link assessment outcomes
    const reqs = await this.getReviewRequirements(reviewId, orgId);
    return reqs as unknown as ComplianceReviewAssessment[];
  },

  // Findings
  async getFindings(reviewId?: string, orgId: string = 'ORG-XYZ'): Promise<ComplianceFinding[]> {
    const url = reviewId
      ? `${API_BASE}/findings?organization_id=${orgId}&review_id=${reviewId}`
      : `${API_BASE}/findings?organization_id=${orgId}`;
    const res = await fetch(url);
    if (!res.ok) throw new Error('Bulgular alinamadi');
    return res.json();
  },

  async createFinding(finding: Partial<ComplianceFinding>, orgId: string = 'ORG-XYZ'): Promise<ComplianceFinding> {
    const res = await fetch(`${API_BASE}/findings`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'x-organization-id': orgId },
      body: JSON.stringify(finding)
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || 'Bulgu olusturulamadi');
    }
    return res.json();
  },

  async linkFinding(finding: Partial<ComplianceFinding>, orgId: string = 'ORG-XYZ'): Promise<ComplianceFinding> {
    return this.createFinding(finding, orgId);
  },

  // Actions
  async getActions(findingId?: string, orgId: string = 'ORG-XYZ'): Promise<ComplianceAction[]> {
    const url = findingId
      ? `${API_BASE}/actions?organization_id=${orgId}&finding_id=${findingId}`
      : `${API_BASE}/actions?organization_id=${orgId}`;
    const res = await fetch(url);
    if (!res.ok) throw new Error('Aksiyonlar alinamadi');
    return res.json();
  },

  async createAction(action: Partial<ComplianceAction>, orgId: string = 'ORG-XYZ'): Promise<ComplianceAction> {
    const res = await fetch(`${API_BASE}/actions`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'x-organization-id': orgId },
      body: JSON.stringify(action)
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || 'Aksiyon olusturulamadi');
    }
    return res.json();
  },

  async linkAction(action: Partial<ComplianceAction>, orgId: string = 'ORG-XYZ'): Promise<ComplianceAction> {
    return this.createAction(action, orgId);
  },

  // Reports
  async getReports(orgId: string = 'ORG-XYZ'): Promise<ComplianceReport[]> {
    const res = await fetch(`${API_BASE}/reports?organization_id=${orgId}`);
    if (!res.ok) throw new Error('Raporlar alinamadi');
    return res.json();
  },

  async createReport(report: Partial<ComplianceReport>, orgId: string = 'ORG-XYZ'): Promise<ComplianceReport> {
    const res = await fetch(`${API_BASE}/reports`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'x-organization-id': orgId },
      body: JSON.stringify(report)
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || 'Rapor olusturulamadi');
    }
    return res.json();
  },

  // Audit History
  async getReviewStatusHistory(reviewId: string, orgId: string = 'ORG-XYZ'): Promise<ComplianceReviewStatusHistory[]> {
    const res = await fetch(`${API_BASE}/reviews/${reviewId}/history?organization_id=${orgId}`);
    if (!res.ok) throw new Error('Tarihce alinamadi');
    return res.json();
  },

  async getStatusHistory(reviewId: string, orgId: string = 'ORG-XYZ'): Promise<ComplianceReviewStatusHistory[]> {
    return this.getReviewStatusHistory(reviewId, orgId);
  }
};
